import { FileUpload, FileUploadEvent } from 'primeng/fileupload';
import { BehaviorSubject, catchError, firstValueFrom, lastValueFrom, map, Observable, of, Subject, tap } from 'rxjs';

import { AttachmentScope, StorageObjectDto } from '../api/backend-connector/models';
import { AttachmentsService } from '../services/attachments.service';
import { ToastService } from '../services/toast.service';

type AttachmentModel = {
  id: string;
  so: StorageObjectDto;
  name: string;
};
export class AttachmentsUploadHandler {
  constructor(
    private attachmentsService: AttachmentsService,
    private toast: ToastService,
    private context: {
      existent: StorageObjectDto[];
      assetId: string | undefined;
      draft: boolean | undefined;
      scope: AttachmentScope;
      showSuccessToast: boolean;
      mode: 'form' | 'manager';
      canEdit: boolean;
    },
  ) {
    this._existent$ = new BehaviorSubject<AttachmentModel[]>(
      context.existent.map((so) => ({ id: so.id, so, name: so.name })),
    );
    this.existent$ = this._existent$.asObservable();
    this._count$ = new BehaviorSubject<number>(context.existent.length);
    this.count$ = this._count$.asObservable();
    this._assetId = context.assetId;
    this._currentAttachments$ = new BehaviorSubject(context.existent);
    this.currentAttachments$ = this._currentAttachments$.asObservable();
  }

  private _fileUpload?: FileUpload;
  private _assetId?: string;

  // private readonly _canSubmit$ = new BehaviorSubject<boolean>(false);
  private readonly _progress$ = new BehaviorSubject<number | undefined>(undefined);
  // private readonly _detectChanges$ = new BehaviorSubject<void>(undefined);
  private readonly _uploadedFileCount$ = new BehaviorSubject<number>(0);
  private readonly _count$;
  private readonly _existent$;
  private readonly _hasRetry$ = new BehaviorSubject<boolean>(false);
  private readonly _created$ = new BehaviorSubject<(AttachmentModel & { file: File })[]>([]);
  private readonly _currentAttachments$: BehaviorSubject<StorageObjectDto[]>;

  readonly progress$ = this._progress$.asObservable();
  // readonly detectChanges$ = this._detectChanges$.asObservable();
  // readonly canSubmit$ = this._canSubmit$.asObservable();
  readonly uploadedFileCount$ = this._uploadedFileCount$.asObservable();
  readonly count$;
  readonly existent$;
  readonly currentAttachmentsChanged$ = new Subject<StorageObjectDto[]>();
  readonly hasRetry$ = this._hasRetry$.asObservable();

  readonly currentAttachments$;

  get isNameEditable() {
    return this.context.mode === 'form';
  }

  get canEdit() {
    return this.context.canEdit;
  }

  setFileUpload(fileUpload?: FileUpload) {
    // clear all
    if (fileUpload) {
      fileUpload.clear();
      fileUpload.clearInputElement();
      fileUpload.uploadedFiles = [];
      fileUpload.cd.markForCheck();
    }
    this._fileUpload = fileUpload;
  }

  getFileUpload() {
    return this._fileUpload;
  }

  setAssetId(assetId: string) {
    this._assetId = assetId;
  }

  isImage(type: string) {
    return this._fileUpload?.isImage({ type } as File);
  }

  // setCanSubmit(value: boolean) {
  //   this._canSubmit$.next(value);
  // }

  clear() {
    this._fileUpload?.clear();
    this._hasRetry$.next(false);
  }

  removeFile(event: MouseEvent, index: number) {
    this._fileUpload?.remove(event, index);
    if (!this._fileUpload?.files?.length) {
      // this.setCanSubmit(false);
    }
  }

  async removeUploaded(index: number) {
    if (this.context.mode === 'manager') {
      try {
        await firstValueFrom(this.attachmentsService.deleteAttachment({ id: this._created$.value[index].so.id }));
        this.attachmentsService.showDeleteSuccessToast(this.toast);
      } catch {
        this.attachmentsService.showDeleteFailToast(this.toast);
        return;
      }
    }

    const created = [...this._created$.value];
    created.splice(index, 1);
    this._created$.next(created);
    this.updateCurrentAttachments();

    this._fileUpload?.removeUploadedFile(index);
    if (!this._fileUpload?.files?.length) {
      // this.setCanSubmit(false);
    }
    this._count$.next(this._count$.value - 1);
  }

  async removeExistent(attachment: StorageObjectDto) {
    if (this.context.mode === 'manager') {
      try {
        await firstValueFrom(this.attachmentsService.deleteAttachment({ id: attachment.id }));
        this.attachmentsService.showDeleteSuccessToast(this.toast);
      } catch {
        this.attachmentsService.showDeleteFailToast(this.toast);
        return;
      }
    }

    this._existent$.next(this._existent$.value.filter((a) => a.id !== attachment.id));
    this._count$.next(this._count$.value - 1);
    this.updateCurrentAttachments();
  }

  private setProgress(value: number) {
    if (this._fileUpload) {
      this._fileUpload.progress = value;
      this._fileUpload.cd.markForCheck();
    }
    this._progress$.next(value);
  }

  private addUploadedFileCount(fileUpload: FileUpload, uploaded: number) {
    this._uploadedFileCount$.next(this._uploadedFileCount$.value + uploaded);
    fileUpload.uploadedFileCount = this._uploadedFileCount$.value;
    this._count$.next(this._count$.value + uploaded);
  }

  async startUpload() {
    const fileUpload = this._fileUpload;
    if (!fileUpload) {
      return;
    }

    fileUpload.uploading = true;
    fileUpload.msgs = [];

    // If the previous upload hasn't been finished, it is aborted.
    // fileUpload.cancelUploadRequest();
    // TODO abort when the onRemove and onClear event occurs

    const totalSize = fileUpload.files.reduce((agg, file) => agg + file.size, 0);
    let uploadedSize = 0;

    const obs: Observable<{ file: File; so?: StorageObjectDto }>[] = fileUpload.files.map((file) =>
      this.attachmentsService
        .uploadFile({
          assetId: this._assetId,
          scope: this.context.scope,
          draft: this.context.draft,
          body: { file },
        })
        .pipe(
          tap(() => {
            uploadedSize = uploadedSize + file.size;
            this.setProgress((uploadedSize * 100) / totalSize);
            this.addUploadedFileCount(fileUpload, 1);
            fileUpload.cd.markForCheck();
          }),
          map((so) => ({ so, file })),
          catchError(() => {
            return of({ file, so: undefined });
          }),
        ),
    );

    let result: { file: File; so?: StorageObjectDto }[] = [];
    try {
      result = await Promise.all(obs.map((ob) => lastValueFrom(ob)));
      const createdFiles = result.filter((f) => f.so).map((f) => f.file);
      const createdStorageObjects = result
        .filter((f): f is { file: File; so: StorageObjectDto } => !!f.so)
        .map(({ file, so }) => ({
          id: so.id,
          so: so,
          name: so.name,
          file,
        }));
      const remainingFiles = result.filter((f) => !f.so).map((f) => f.file);
      this._hasRetry$.next(remainingFiles.length > 0);
      fileUpload.uploading = false;
      this.setProgress(0);

      // if (fileUpload.fileLimit) {
      //   fileUpload.uploadedFileCount += createdFiles.length;
      // }

      fileUpload.onUpload.emit({ originalEvent: undefined, files: fileUpload.files } as unknown as FileUploadEvent);

      fileUpload.uploadedFiles.push(...createdFiles);
      this._created$.next([...this._created$.value, ...createdStorageObjects]);
      this.updateCurrentAttachments();

      fileUpload.clear();
      fileUpload.files = remainingFiles;

      if (remainingFiles.length) {
        this.attachmentsService.showCreateFailToast(this.toast);
      } else if (this.context.showSuccessToast) {
        void this.attachmentsService.showCreateSuccessToast(this.toast);
      }
    } catch (error) {
      // UNREACHABLE
      // fileUpload.uploading = false;
      // fileUpload.onError.emit({ files: fileUpload.files, error: error as unknown as ErrorEvent });
      // this.setProgress(0);
    }
    fileUpload.cd.markForCheck();
  }

  getUploadedStorageObject(file: File) {
    return this._created$.value.find((f) => f.file === file)!.so;
  }

  updateName(id: string, value: string) {
    if (this.context.mode === 'manager') {
      throw new Error('NYI');
    }
    const currentAttachments = [
      ...this._existent$.value.map(({ so }) => ({ ...so, name: so.id === id ? value : so.name })),
      ...this._created$.value.map(({ so }) => ({ ...so, name: so.id === id ? value : so.name })),
    ];
    this.setCurrentAttachments(currentAttachments);
  }

  private updateCurrentAttachments() {
    const currentAttachments = [
      ...this._existent$.value.map(({ so }) => so),
      ...this._created$.value.map(({ so }) => so),
    ];
    this.setCurrentAttachments(currentAttachments);
  }

  private setCurrentAttachments(currentAttachments: StorageObjectDto[]) {
    this.currentAttachmentsChanged$.next(currentAttachments);
    this._currentAttachments$.next(currentAttachments);
  }
}
